<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAcesso } from 'dashboard/composables/useAcesso';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import CostPanel from '../analytics/CostPanel.vue';
import DailyCharts from '../analytics/DailyCharts.vue';
import ErrorsPanel from '../analytics/ErrorsPanel.vue';
import FollowupsPanel from '../analytics/FollowupsPanel.vue';
import MetricValue from '../analytics/MetricValue.vue';
import ProportionBar from '../analytics/ProportionBar.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import AdicionarABase from './AdicionarABase.vue';
import { reasonLabel } from './atendimentoReasons';

// "A IA": os números da Analytics dos agentes com o que faltava nela — a
// ferramenta que falhou, o motivo das passagens e a pergunta que a base não
// soube responder, com o atalho para a base. Abaixo, o que a Analytics antiga
// mostrava e que agora mora aqui (REPORTS-03): a IA por dia, os erros, os
// follow-ups e o custo, cada um no próprio bloco, irmão deste. O custo só vem na
// resposta de super admin: a tabela e o bloco o mostram pela presença da chave,
// nunca pelo papel.
const props = defineProps({
  // A resposta de GET atendimento/ia; null enquanto não chegou.
  report: { type: Object, default: null },
  failed: { type: Boolean, default: false },
  // A resposta de GET atendimento/ferramentas: as chamadas de ferramenta do
  // runtime, pedidas à parte; null enquanto não chegou.
  tools: { type: Object, default: null },
  toolsFailed: { type: Boolean, default: false },
  // Time ou atendente no filtro: o runtime não se separa por eles.
  partialFilters: { type: Boolean, default: false },
});

const { t } = useI18n();
const { number, percent, duration, usd } = useAnalyticsFormat();
const { isAdmin } = useAdmin();
// ACESSO-01 (L8b): "Adicionar à base" grava uma fonte na base do agente (`power_data#sources_create`): com a chave
// ACESSO_GRANULAR ligada, o `powerdata:write`; desligada, o administrador, como hoje.
const { decide } = useAcesso();
const adicionaABase = computed(() =>
  decide('powerdata:write', () => isAdmin.value)
);

// As categorias que ganham barra própria; o resto soma em "Outros".
const CATEGORIES_SHOWN = 4;

const noAgentsReason = computed(() =>
  props.report?.no_data_reason
    ? reasonLabel(t, props.report.no_data_reason)
    : ''
);
const totals = computed(() => props.report?.totals || {});
const runtimeDown = computed(() => props.report?.runtime?.available === false);
const runtimeReason = computed(() =>
  runtimeDown.value ? reasonLabel(t, 'runtime') : ''
);

// As falhas sobre as chamadas do período; "ver quais" abre a lista das
// ferramentas que falharam, da que mais falhou para a que menos falhou.
const toolCalls = computed(() => props.tools?.tools || null);
const failedTools = computed(() => toolCalls.value?.per_tool || []);
const showTools = ref(false);

const TOOL_STATUS = {
  error: 'ATENDIMENTO_REPORTS.AI.TOOL_STATUS.ERROR',
  timeout: 'ATENDIMENTO_REPORTS.AI.TOOL_STATUS.TIMEOUT',
  invalid_args: 'ATENDIMENTO_REPORTS.AI.TOOL_STATUS.INVALID_ARGS',
  circuit_open: 'ATENDIMENTO_REPORTS.AI.TOOL_STATUS.CIRCUIT_OPEN',
};
const toolStatuses = tool =>
  Object.entries(tool.failures_by_status || {})
    .filter(([, count]) => count > 0)
    .map(([status, count]) =>
      t('ATENDIMENTO_REPORTS.AI.TOOL_STATUS_COUNT', {
        status: TOOL_STATUS[status] ? t(TOOL_STATUS[status]) : status,
        count: number(count),
      })
    )
    .join(' · ');
// Quem é o agente da ferramenta e como ela falhou.
const toolDetail = tool =>
  [tool.agent, toolStatuses(tool)].filter(Boolean).join(' · ');

const share = (part, whole) =>
  typeof part === 'number' && whole > 0
    ? Math.round((part / whole) * 100)
    : null;

const kpis = computed(() => {
  const wait = totals.value.wait_seconds || {};
  const turns = totals.value.turns;
  const knowledge = totals.value.knowledge;
  return [
    {
      key: 'response_time',
      label: t('ATENDIMENTO_REPORTS.AI.RESPONSE_TIME'),
      value: wait.samples ? wait.p50 : null,
      format: duration,
      reason: wait.samples ? '' : reasonLabel(t, 'no_samples'),
      hint:
        typeof wait.p90 === 'number' && wait.samples
          ? t('ATENDIMENTO_REPORTS.AI.RESPONSE_TIME_HINT', {
              p90: duration(wait.p90),
            })
          : '',
    },
    {
      key: 'turn_errors',
      label: t('ATENDIMENTO_REPORTS.AI.TURN_ERRORS'),
      value: turns > 0 ? totals.value.turn_errors : null,
      format: number,
      reason:
        runtimeReason.value || (turns === 0 ? reasonLabel(t, 'no_turns') : ''),
      hint:
        turns > 0
          ? t('ATENDIMENTO_REPORTS.AI.TURN_ERRORS_HINT', {
              total: number(turns),
            })
          : '',
    },
    {
      key: 'failed_tools',
      label: t('ATENDIMENTO_REPORTS.AI.FAILED_TOOLS'),
      value: toolCalls.value?.failures ?? null,
      format: number,
      loading: !props.tools && !props.toolsFailed,
      reason: reasonLabel(
        t,
        props.toolsFailed ? 'load_failed' : toolCalls.value?.reason
      ),
      hint:
        toolCalls.value?.calls > 0
          ? t('ATENDIMENTO_REPORTS.AI.FAILED_TOOLS_HINT', {
              total: number(toolCalls.value.calls),
            })
          : '',
      toggle: failedTools.value.length > 0,
    },
    {
      key: 'knowledge',
      label: t('ATENDIMENTO_REPORTS.AI.KNOWLEDGE_MISSES'),
      value: knowledge?.searches > 0 ? knowledge.empty : null,
      format: number,
      reason:
        runtimeReason.value ||
        (knowledge?.searches === 0 ? reasonLabel(t, 'no_searches') : ''),
      hint:
        knowledge?.searches > 0
          ? t('ATENDIMENTO_REPORTS.AI.KNOWLEDGE_MISSES_HINT', {
              total: number(knowledge.searches),
            })
          : '',
    },
  ];
});

// As categorias mais frequentes com barra, e o resto somado em "Outros".
const categories = computed(() => {
  const all = props.report?.handoff_categories?.categories || [];
  const shown = all.slice(0, CATEGORIES_SHOWN).map(item => ({
    key: item.category || '—',
    label: item.category || t('ATENDIMENTO_REPORTS.AI.NO_CATEGORY'),
    count: item.count,
  }));
  const rest = all.slice(CATEGORIES_SHOWN).reduce((sum, i) => sum + i.count, 0);
  return rest
    ? [
        ...shown,
        {
          key: 'outros',
          label: t('ATENDIMENTO_REPORTS.AI.OTHER'),
          count: rest,
        },
      ]
    : shown;
});
const categoriesTotal = computed(
  () => props.report?.handoff_categories?.total ?? 0
);
const biggestCategory = computed(() =>
  Math.max(...categories.value.map(item => item.count), 0)
);

const gaps = computed(() => props.report?.knowledge_gaps);
const agents = computed(() => props.report?.per_agent || []);
const hasCost = computed(() => agents.value.some(row => 'cost_usd' in row));

// Os blocos da Analytics antiga só com a resposta de agentes na mão.
const showPanels = computed(
  () => Boolean(props.report) && !props.failed && !noAgentsReason.value
);
const period = computed(() => props.report?.period || {});
const agentNames = computed(() =>
  Object.fromEntries(
    (props.report?.agents || []).map(agent => [agent.slug, agent.name])
  )
);
const hasCostPanel = computed(() =>
  Boolean(props.report && 'cost' in props.report)
);
</script>

<template>
  <AnalyticsPanel
    data-test-id="atendimento-ia"
    :title="t('ATENDIMENTO_REPORTS.AI.TITLE')"
    :description="t('ATENDIMENTO_REPORTS.AI.DESCRIPTION')"
  >
    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.FAILED') }}
    </p>

    <MetricValue
      v-else-if="noAgentsReason"
      data-test-id="atendimento-ia-no-data"
      :reason="noAgentsReason"
    />

    <template v-else-if="report">
      <p
        v-if="runtimeDown"
        data-test-id="atendimento-ia-runtime-down"
        class="px-3 py-2 mb-0 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
      >
        {{ t('ATENDIMENTO_REPORTS.AI.RUNTIME_DOWN') }}
      </p>
      <p
        v-if="partialFilters"
        data-test-id="atendimento-ia-partial-filters"
        class="mb-0 text-xs text-n-slate-11"
      >
        {{ t('ATENDIMENTO_REPORTS.AI.IGNORES_FILTERS') }}
      </p>

      <div class="grid grid-cols-2 gap-3 min-w-0 lg:grid-cols-4">
        <div
          v-for="kpi in kpis"
          :key="kpi.key"
          :data-test-id="`atendimento-ia-${kpi.key}`"
          class="flex flex-col gap-1 p-3 min-w-0 rounded-lg border border-n-weak"
        >
          <span class="text-sm text-n-slate-11">{{ kpi.label }}</span>
          <Spinner
            v-if="kpi.loading"
            :data-test-id="`atendimento-ia-${kpi.key}-loading`"
            :size="16"
            class="my-2 text-n-slate-10"
          />
          <MetricValue
            v-else
            :data-test-id="`atendimento-ia-${kpi.key}-value`"
            :value="kpi.value"
            :format="kpi.format"
            :reason="kpi.reason"
            class="text-2xl font-medium tabular-nums break-words text-n-slate-12"
          />
          <span v-if="kpi.hint" class="text-xs text-n-slate-11">
            {{ kpi.hint }}
          </span>
          <Button
            v-if="kpi.toggle"
            :data-test-id="`atendimento-ia-${kpi.key}-toggle`"
            :label="
              showTools
                ? t('ATENDIMENTO_REPORTS.AI.HIDE_TOOLS')
                : t('ATENDIMENTO_REPORTS.AI.SHOW_TOOLS')
            "
            link
            xs
            type="button"
            class="self-start"
            @click="showTools = !showTools"
          />
        </div>
      </div>

      <section
        v-if="showTools && failedTools.length"
        data-test-id="atendimento-ia-ferramentas"
        class="flex flex-col gap-2 p-3 min-w-0 rounded-lg border border-n-weak"
      >
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('ATENDIMENTO_REPORTS.AI.TOOLS_TITLE') }}
        </h3>
        <ul class="flex flex-col list-none divide-y divide-n-weak">
          <li
            v-for="tool in failedTools"
            :key="`${tool.agent_id}-${tool.tool}`"
            class="flex flex-wrap gap-x-3 gap-y-0.5 justify-between items-baseline py-2 min-w-0 text-sm first:pt-0"
          >
            <span class="flex flex-col min-w-0">
              <span class="break-words text-n-slate-12">{{ tool.tool }}</span>
              <span class="text-xs text-n-slate-11">
                {{ toolDetail(tool) }}
              </span>
            </span>
            <span class="whitespace-nowrap tabular-nums text-n-slate-12">
              {{
                t('ATENDIMENTO_REPORTS.AI.TOOL_FAILURES', {
                  failures: number(tool.failures),
                  calls: number(tool.calls),
                })
              }}
            </span>
          </li>
        </ul>
      </section>

      <div class="grid gap-4 min-w-0 lg:grid-cols-2">
        <section
          data-test-id="atendimento-ia-categorias"
          class="flex flex-col gap-3 p-3 min-w-0 rounded-lg border border-n-weak"
        >
          <header class="flex flex-wrap gap-2 justify-between items-baseline">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ t('ATENDIMENTO_REPORTS.AI.WHY_HANDOFF') }}
            </h3>
            <span class="text-xs text-n-slate-11">
              {{
                t(
                  'ATENDIMENTO_REPORTS.AI.HANDOFFS_COUNT',
                  { count: number(categoriesTotal) },
                  categoriesTotal
                )
              }}
            </span>
          </header>
          <MetricValue
            v-if="!categoriesTotal"
            :reason="reasonLabel(t, 'no_handoffs')"
          />
          <ul v-else class="flex flex-col gap-2 list-none">
            <li
              v-for="item in categories"
              :key="item.key"
              class="grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)_auto] gap-3 items-center text-sm"
            >
              <span class="truncate text-n-slate-12">{{ item.label }}</span>
              <ProportionBar
                class="h-2"
                :segments="[
                  { key: item.key, value: item.count, fill: 'fill-n-amber-9' },
                ]"
                :total="biggestCategory"
              />
              <span class="tabular-nums text-n-slate-12">
                {{ number(item.count) }}
              </span>
            </li>
          </ul>
        </section>

        <section
          data-test-id="atendimento-ia-lacunas"
          class="flex flex-col gap-3 p-3 min-w-0 rounded-lg border border-n-weak"
        >
          <header class="flex flex-wrap gap-2 justify-between items-baseline">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ t('ATENDIMENTO_REPORTS.AI.GAPS_TITLE') }}
            </h3>
            <span class="text-xs text-n-slate-11">
              {{ t('ATENDIMENTO_REPORTS.AI.GAPS_HINT') }}
            </span>
          </header>
          <MetricValue v-if="gaps === null" :reason="runtimeReason" />
          <p v-else-if="!gaps.length" class="mb-0 text-sm text-n-slate-11">
            {{ t('ATENDIMENTO_REPORTS.AI.GAPS_EMPTY') }}
          </p>
          <ul v-else class="flex flex-col list-none divide-y divide-n-weak">
            <li
              v-for="gap in gaps"
              :key="gap.question"
              class="flex gap-3 justify-between items-start py-2 min-w-0 first:pt-0"
            >
              <span class="min-w-0 text-sm break-words text-n-slate-12">
                {{
                  t('ATENDIMENTO_REPORTS.AI.GAP_QUESTION', {
                    question: gap.question,
                  })
                }}
                <span class="text-xs text-n-slate-11">
                  {{
                    t(
                      'ATENDIMENTO_REPORTS.AI.GAPS_TIMES',
                      { count: number(gap.count) },
                      gap.count
                    )
                  }}
                </span>
              </span>
              <AdicionarABase
                v-if="adicionaABase && gap.agent_slug"
                :question="gap.question"
                :agent-slug="gap.agent_slug"
              />
            </li>
          </ul>
        </section>
      </div>

      <div data-test-id="atendimento-ia-agentes" class="min-w-0">
        <table class="hidden w-full text-sm md:table">
          <thead>
            <tr class="text-xs text-start text-n-slate-11">
              <th class="py-2 font-medium text-start">
                {{ t('ATENDIMENTO_REPORTS.AI.PER_AGENT.AGENT') }}
              </th>
              <th class="py-2 font-medium text-end">
                {{ t('ATENDIMENTO_REPORTS.AI.PER_AGENT.CONVERSATIONS') }}
              </th>
              <th class="py-2 font-medium text-end">
                {{ t('ATENDIMENTO_REPORTS.AI.PER_AGENT.FINISHED') }}
              </th>
              <th class="py-2 font-medium text-end">
                {{ t('ATENDIMENTO_REPORTS.AI.PER_AGENT.HANDED_OFF') }}
              </th>
              <th class="py-2 font-medium text-end">
                {{ t('ATENDIMENTO_REPORTS.AI.PER_AGENT.RESPONSE_TIME') }}
              </th>
              <th class="py-2 font-medium text-end">
                {{ t('ATENDIMENTO_REPORTS.AI.PER_AGENT.ERRORS') }}
              </th>
              <th v-if="hasCost" class="py-2 font-medium text-end">
                {{ t('ATENDIMENTO_REPORTS.AI.PER_AGENT.COST') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="row in agents"
              :key="row.id"
              :data-test-id="`atendimento-ia-agente-${row.id}`"
            >
              <td class="py-2 font-medium text-n-slate-12">{{ row.name }}</td>
              <td class="py-2 tabular-nums text-end text-n-slate-12">
                {{ number(row.conversations) }}
              </td>
              <td class="py-2 tabular-nums text-end text-n-slate-12">
                {{ number(row.finished_without_person) }}
                <span
                  v-if="
                    share(row.finished_without_person, row.conversations) !==
                    null
                  "
                  class="block text-xs text-n-slate-11"
                >
                  {{
                    percent(
                      share(row.finished_without_person, row.conversations)
                    )
                  }}
                </span>
              </td>
              <td class="py-2 tabular-nums text-end text-n-slate-12">
                {{ number(row.handed_off) }}
                <span
                  v-if="share(row.handed_off, row.conversations) !== null"
                  class="block text-xs text-n-slate-11"
                >
                  {{ percent(share(row.handed_off, row.conversations)) }}
                </span>
              </td>
              <td class="py-2 tabular-nums text-end text-n-slate-12">
                <MetricValue
                  :value="
                    row.wait_seconds.samples ? row.wait_seconds.p50 : null
                  "
                  :format="duration"
                />
                <span
                  v-if="row.wait_seconds.samples"
                  class="block text-xs text-n-slate-11"
                >
                  {{
                    t('ATENDIMENTO_REPORTS.AI.PER_AGENT.P90', {
                      value: duration(row.wait_seconds.p90),
                    })
                  }}
                </span>
              </td>
              <td class="py-2 tabular-nums text-end text-n-slate-12">
                <MetricValue :value="row.turn_errors" :format="number" />
              </td>
              <td
                v-if="hasCost"
                class="py-2 tabular-nums text-end text-n-slate-12"
              >
                <MetricValue :value="row.cost_usd" :format="usd" />
              </td>
            </tr>
          </tbody>
        </table>

        <ul class="flex flex-col gap-2 list-none md:hidden">
          <li
            v-for="row in agents"
            :key="row.id"
            class="flex flex-col gap-1 p-3 text-sm rounded-lg border border-n-weak"
          >
            <span class="font-medium text-n-slate-12">{{ row.name }}</span>
            <span class="flex justify-between gap-3 text-n-slate-11">
              {{ t('ATENDIMENTO_REPORTS.AI.PER_AGENT.CONVERSATIONS') }}
              <span class="tabular-nums text-n-slate-12">
                {{ number(row.conversations) }}
              </span>
            </span>
            <span class="flex justify-between gap-3 text-n-slate-11">
              {{ t('ATENDIMENTO_REPORTS.AI.PER_AGENT.FINISHED') }}
              <span class="tabular-nums text-n-slate-12">
                {{ number(row.finished_without_person) }}
              </span>
            </span>
            <span class="flex justify-between gap-3 text-n-slate-11">
              {{ t('ATENDIMENTO_REPORTS.AI.PER_AGENT.HANDED_OFF') }}
              <span class="tabular-nums text-n-slate-12">
                {{ number(row.handed_off) }}
              </span>
            </span>
            <span class="flex justify-between gap-3 text-n-slate-11">
              {{ t('ATENDIMENTO_REPORTS.AI.PER_AGENT.RESPONSE_TIME') }}
              <MetricValue
                class="tabular-nums text-n-slate-12"
                :value="row.wait_seconds.samples ? row.wait_seconds.p50 : null"
                :format="duration"
              />
            </span>
            <span class="flex justify-between gap-3 text-n-slate-11">
              {{ t('ATENDIMENTO_REPORTS.AI.PER_AGENT.ERRORS') }}
              <MetricValue
                class="tabular-nums text-n-slate-12"
                :value="row.turn_errors"
                :format="number"
              />
            </span>
            <span
              v-if="hasCost"
              class="flex justify-between gap-3 text-n-slate-11"
            >
              {{ t('ATENDIMENTO_REPORTS.AI.PER_AGENT.COST') }}
              <MetricValue
                class="tabular-nums text-n-slate-12"
                :value="row.cost_usd"
                :format="usd"
              />
            </span>
          </li>
        </ul>
      </div>
    </template>
  </AnalyticsPanel>

  <template v-if="showPanels">
    <DailyCharts
      :daily="report.daily ?? null"
      :since="period.since"
      :until="period.until"
    />
    <div class="grid gap-4 min-w-0 lg:grid-cols-2">
      <ErrorsPanel
        :errors="totals.errors ?? null"
        :items="report.errors ?? null"
        :agent-names="agentNames"
        :timezone="period.timezone"
        :runtime-reason="runtimeReason"
      />
      <FollowupsPanel
        :followups="totals.followups ?? null"
        :runtime-reason="runtimeReason"
      />
    </div>
    <CostPanel
      v-if="hasCostPanel"
      :cost="report.cost"
      :agent-names="agentNames"
      :since="period.since"
      :until="period.until"
      :runtime-reason="runtimeReason"
    />
  </template>
</template>
